/*
 * CSS accordion toggle — Osmo setup.
 * https://www.osmo.supply/resource/accordion-css-animation
 *
 * The open/close height animation is pure CSS (grid-template-rows 0fr → 1fr, keyed off
 * [data-accordion-status="active"]). This just toggles that status + aria-expanded, and
 * optionally closes sibling items ([data-accordion-close-siblings="true"] for single-open).
 * Closed panels are `inert` so their content can't be focused or announced; the markup ships
 * them inert, and this keeps that in sync.
 *
 * <wp-accordion> (display: contents) wraps a [data-accordion] list, so a section re-rendered in the
 * theme editor re-binds in connectedCallback and drops its listeners in disconnectedCallback. Shared
 * by the FAQ and printing-services sections. Functional, not decorative, so it isn't gated on
 * prefers-reduced-motion — the CSS drops the height transition under reduce instead.
 */
(function () {
  if (customElements.get('wp-accordion')) return;

  const getPanel = (toggle) => document.getElementById(toggle.getAttribute('aria-controls'));

  // The accordion changes page height, so later ScrollTriggers (e.g. the footer parallax) need to
  // re-measure once the height transition has finished. Coalesced to one refresh per frame, since
  // close-siblings ends two transitions at once (and across accordions).
  let refreshQueued = false;
  const queueScrollTriggerRefresh = () => {
    if (!window.ScrollTrigger || refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      window.ScrollTrigger.refresh();
    });
  };

  const setOpen = (item, open) => {
    item.setAttribute('data-accordion-status', open ? 'active' : 'not-active');
    const toggle = item.querySelector('[data-accordion-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    const panel = getPanel(toggle);
    if (!panel) return;
    panel.inert = !open;
    // No transition (e.g. reduced-motion overrides) means no transitionend, so refresh now.
    if (parseFloat(getComputedStyle(panel).transitionDuration) === 0) queueScrollTriggerRefresh();
  };

  class WpAccordion extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
    }

    connectedCallback() {
      this.accordion = this.querySelector('[data-accordion]');
      if (!this.accordion) return;
      this.closeSiblings = this.accordion.getAttribute('data-accordion-close-siblings') === 'true';

      // Sync panels with their item's status in case markup and state ever disagree.
      this.accordion.querySelectorAll('[data-accordion-toggle]').forEach((toggle) => {
        const panel = getPanel(toggle);
        const item = toggle.closest('[data-accordion-status]');
        if (panel && item) panel.inert = item.getAttribute('data-accordion-status') !== 'active';
      });

      this.accordion.addEventListener('click', this.onClick);
      this.accordion.addEventListener('transitionend', this.onTransitionEnd);
    }

    disconnectedCallback() {
      if (!this.accordion) return;
      this.accordion.removeEventListener('click', this.onClick);
      this.accordion.removeEventListener('transitionend', this.onTransitionEnd);
      this.accordion = null;
    }

    onClick(event) {
      const toggle = event.target.closest('[data-accordion-toggle]');
      if (!toggle || !this.accordion.contains(toggle)) return;

      const item = toggle.closest('[data-accordion-status]');
      if (!item) return;

      const isActive = item.getAttribute('data-accordion-status') === 'active';
      setOpen(item, !isActive);

      if (this.closeSiblings && !isActive) {
        this.accordion.querySelectorAll('[data-accordion-status="active"]').forEach((sibling) => {
          if (sibling !== item) setOpen(sibling, false);
        });
      }
    }

    onTransitionEnd(event) {
      if (event.propertyName !== 'grid-template-rows' || event.target.getAttribute('role') !== 'region') return;
      queueScrollTriggerRefresh();
    }
  }

  customElements.define('wp-accordion', WpAccordion);
})();
